// Vendors
import { useEffect, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
// Contexts
import { useAppContextConsumerHook } from 'contexts/app.context';
// Constants
import { ERROR_CUSTOM_ACTION } from './constants/error-log.hook.constants';
// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
// Event Handlers
import { ErrorStateHandlers } from '../handlers/error-state.handlers';
import { ErrorHandlers } from '../handlers/error.handlers';
import { ErrorTrackingHandlers } from '../handlers/error.tracking.handlers';
import { LogHandlers } from 'handlers/log.handlers';
// Hooks
import { useCallbackBackHook } from 'hooks/callback-back.hook';
import { useAppGetStateHook } from 'hooks/get-state.hook';
import { useAppTrackingHook } from 'tracking/app-tracking.hook';
// Types
import { ErrorHookType } from './types/error.hook.type';

const useErrorHook = (mappedError: AppErrorEnumeration): ErrorHookType => {
  const [showModal, setShowModal] = useState<boolean>(false);

  const { setCurrentState } = useAppContextConsumerHook(),
    { resetBoundary } = useErrorBoundary();

  const { handleLogCallbackUrlCallEvent } = useCallbackBackHook({ resetBoundary }),
    {
      handleManageFailGetStateFromErrorEvent: onError,
      handleManageSuccessGetStateFromErrorEvent: onSuccess,
    } = useMemo<ReturnType<typeof ErrorStateHandlers>>(
      () => ErrorStateHandlers({ handleLogCallbackUrlCallEvent, resetBoundary, setCurrentState }),
      []
    ),
    { handleSendCustomActionEvent } = LogHandlers(),
    { handleGetOnboardingStateEvent } = useAppGetStateHook({ onError, onSuccess }),
    { handleTrackEnterErrorView, ...trackingHandlers } = useAppTrackingHook(ErrorTrackingHandlers),
    {
      handleCloseCancelApplicationModal,
      handleManageErrorButtonClick,
      handleShowCancelApplicationModal,
    } = ErrorHandlers({
      handleSendCustomActionEvent,
      handleGetOnboardingStateEvent,
      handleLogCallbackUrlCallEvent,
      mappedError,
      resetBoundary,
      setCurrentState,
      setShowModal,
    });

  useEffect(() => {
    if (mappedError) {
      handleSendCustomActionEvent(`${ERROR_CUSTOM_ACTION}: ${mappedError}`);
      handleTrackEnterErrorView(mappedError);
    }
  }, [mappedError]);

  return {
    ...trackingHandlers,
    handleCloseCancelApplicationModal,
    handleManageErrorButtonClick,
    handleShowCancelApplicationModal,
    showModal,
  };
};

export { useErrorHook };
